/**
 * Consolidated invoicing queries (#3933).
 *
 * Live presentation comes from the staff-api routes. A finalized invoice may
 * also carry the same rows frozen on contentSnapshot; the page prefers that
 * copy. Money stays integer cents end to end.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from './queryKeys';
import {
  normalizeCustomerReference,
  type InvoiceConsolidation,
} from '@/components/workorders/consolidation/presentation';

export interface WorkOrderConsolidationLink {
  invoiceId: string;
  invoiceNumber: string | null;
  invoiceStatus: string;
  periodStart: string | null;
  periodEnd: string | null;
  customerReference: string | null;
  attachedAt: string;
}

export interface WorkOrderConsolidationResponse {
  link: WorkOrderConsolidationLink | null;
}

export interface OpenConsolidatedInvoice {
  id: string;
  invoiceNumber: string;
  companyId: string;
  companyName: string;
  workOrderCount: number;
  totalCents: number;
  periodStart: string | null;
  nextCutoffAt: string | null;
  issueDate: string | null;
  kind: 'regular' | 'month_end' | null;
}

export const consolidatedInvoiceKeys = {
  all: ['consolidated-invoices'] as const,
  open: () => [...consolidatedInvoiceKeys.all, 'open'] as const,
  invoice: (invoiceId: string) => [...consolidatedInvoiceKeys.all, 'invoice', invoiceId] as const,
  workOrder: (workOrderId: string) =>
    [...consolidatedInvoiceKeys.all, 'work-order', workOrderId] as const,
};

export function useWorkOrderConsolidation(workOrderId: string, enabled = true) {
  return useQuery({
    queryKey: consolidatedInvoiceKeys.workOrder(workOrderId),
    enabled: enabled && workOrderId.length > 0,
    queryFn: () =>
      api.get<WorkOrderConsolidationResponse>(`/work-orders/${workOrderId}/consolidation`),
    staleTime: 15 * 1000,
  });
}

export function useInvoiceConsolidation(invoiceId: string | undefined) {
  return useQuery({
    queryKey: consolidatedInvoiceKeys.invoice(invoiceId ?? ''),
    enabled: Boolean(invoiceId),
    queryFn: async (): Promise<InvoiceConsolidation | null> => {
      try {
        return await api.get<InvoiceConsolidation>(`/invoices/${invoiceId}/consolidation`);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    staleTime: 15 * 1000,
  });
}

export function useOpenConsolidatedInvoices(enabled = true) {
  return useQuery({
    queryKey: consolidatedInvoiceKeys.open(),
    enabled,
    queryFn: async () => {
      const body = await api.get<{ invoices: OpenConsolidatedInvoice[] }>(
        '/invoices/consolidation/open',
      );
      return body.invoices;
    },
    staleTime: 15 * 1000,
  });
}

export function useDetachWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workOrderId }: { workOrderId: string }) =>
      api.post<{ success: true; invoiceId: string; invoiceRemoved: boolean }>(
        `/work-orders/${workOrderId}/consolidation/detach`,
        {},
      ),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: consolidatedInvoiceKeys.workOrder(variables.workOrderId),
      });
      void queryClient.invalidateQueries({ queryKey: consolidatedInvoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.forWorkOrder(variables.workOrderId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workOrders.detail(variables.workOrderId),
      });
    },
  });
}

export function useCloseConsolidatedInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId }: { invoiceId: string }) =>
      api.post<{ success: true }>(`/invoices/${invoiceId}/consolidation/close`, {}),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.detail(variables.invoiceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({
        queryKey: consolidatedInvoiceKeys.invoice(variables.invoiceId),
      });
      void queryClient.invalidateQueries({ queryKey: consolidatedInvoiceKeys.open() });
    },
  });
}

export function useUpdateConsolidationReference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      bookingId,
      customerReference,
    }: {
      invoiceId: string;
      bookingId: string;
      customerReference: string | null;
    }) => {
      const normalized = normalizeCustomerReference(customerReference);
      if (normalized === undefined) {
        return Promise.reject(new Error('customer reference is too long'));
      }
      return api.patch<{ customerReference: string | null }>(
        `/invoices/${invoiceId}/consolidation/work-orders/${bookingId}`,
        { customerReference: normalized },
      );
    },
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: consolidatedInvoiceKeys.invoice(variables.invoiceId),
      });
      void queryClient.invalidateQueries({
        queryKey: consolidatedInvoiceKeys.workOrder(variables.bookingId),
      });
    },
  });
}
